/**
 * 案件から見た業務フロー 2 枚（変更前 / 変更後）の差分を導出する。
 *
 * **構造の差は 2 枚の骨格の比較から、中身の差は案件の変更の行から取る。** 増えた業務・消えた
 * 業務・付け替わった枝は 2 枚を突き合わせれば分かり、消えた業務が「何手目に居て、どこと
 * 繋がっていたか」も変更前の骨格が持っている。変更の行だけから削除を描こうとすると位置も枝も
 * 決まらず、推測で図に置くことになる。いっぽう中身（欄の値）の書き換えは骨格に現れないので、
 * そこだけは案件の変更の行の前後（`before` / `after`）から取る。
 *
 * **節点の同一性は業務の id（number）で判定する。** 骨格が持つ `nodeId` は**業務フローの中で
 * 1 から振る局所 ID** で、節点が 1 つ増えただけで振り直される。局所 ID で突き合わせると、
 * 変わっていない業務が「消えて増えた」ことになる。業務の id はグラフ全体で一意な番号なので、
 * 2 枚のあいだで安定している。
 *
 * **枝の同一性も `id` ではなく両端の業務で判定する。** `id` は骨格の並び順から作られるので、
 * 節点が 1 つ増えれば全部ずれる。
 *
 * 旧表のフロー差分との違いは鍵の空間だけである。あちらは操作の qualifiedName で
 * 突き合わせていた。作り直しでは節点が業務を直に指し、id が number になったので、
 * 分解を通して鍵を揃える手順そのものが要らなくなった。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更前と変更後の業務フローを突き合わせ、左右 2 枚の図に載せる追加・変更・削除の差分を導出する。
 * @graph-connects buildPjTouchedIndex [calls] 案件の変更の行から差分の状態の索引を畳む
 */
import type { FlowTransitionView, FlowView, PjChangeRow } from '@cortex/domain-spec-console';

import type { FlowChangeState } from './flow-workspace/flow-diff-marks.js';
import { buildPjTouchedIndex, type PjTouchedIndex } from './pj-touched-index.js';

/** 比較するフローの節点と枝。見出しやレーンは差分に要らないので持たない。 @graph-connects none */
export type StoryFlowSkeleton = Pick<FlowView, 'nodes' | 'transitions'>;

/** フローの節点 1 個。 @graph-connects none */
type StoryFlowNode = FlowView['nodes'][number];

/**
 * 案件から見たフロー 2 枚分の差分。
 *
 * 図に載せる 4 つ（節点の状態 2 面ぶん・増えた枝・消えた枝）に加えて、業務の件数と
 * 「流れを変えていない」の判定を持つ。件数と判定は、差分が有るか（「変更前と比較」を押せるか）と、
 * 1 面では出せない削除の件数の断りに使う。図の上に件数は出さない（Dhik 2026-09-29）。
 */
export interface StoryFlowPjDiff {
  /** 変更前の図に載せる状態。鍵は変更前の局所 ID。 */
  beforeStateByNodeId: ReadonlyMap<number, FlowChangeState>;
  /** 変更後の図に載せる状態。鍵は変更後の局所 ID。 */
  afterStateByNodeId: ReadonlyMap<number, FlowChangeState>;
  /** 変更前にしか無い枝の ID。 */
  removedTransitionIds: ReadonlySet<string>;
  /** 変更後にしか無い枝の ID。 */
  addedTransitionIds: ReadonlySet<string>;
  /** 追加された業務の件数。開始 / 終了は数えない。 */
  createdCount: number;
  /** 中身が変わった業務の件数。 */
  updatedCount: number;
  /** 削除された業務の件数。開始 / 終了は数えない。 */
  deletedCount: number;
  /** この案件がこのフローの流れ（節点と枝）を変えていないか。 */
  skeletonUnchanged: boolean;
}

/**
 * 節点 1 個の同一性の鍵。
 *
 * 業務を指す節点は業務の id、開始 / 終了は種別と局所 ID から作る。接頭辞で空間を分けるのは、
 * 業務の id と局所 ID がたまたま同じ数になったときに別物を同じ節点とみなさないためである。
 *
 * 業務が未リンクの節点（`business` なのに参照が無い）は局所 ID で判定するしかない。書き途中の
 * 節点なので、2 枚のあいだで対応が取れなくても「増えた / 減った」として出るほうが安全である。
 *
 * @param node フローの節点
 * @returns 同一性の鍵
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フローの節点の同一性の鍵を、業務の id か、種別つきの局所 ID から作る。
 * @graph-connects none
 */
export function storyNodeIdentityOf(node: StoryFlowNode): string {
  if (node.business !== null) return `business:${String(node.business.id)}`;
  return `${node.kind}:${String(node.nodeId)}`;
}

/**
 * 枝 1 本の同一性の鍵を、両端の節点の同一性から作る。
 *
 * 区切り文字を決め打ちにせず配列を JSON にするのは、業務の id を含む鍵がその区切り文字を
 * 含みうるときに別の組が同じ鍵になるのを避けるためである。
 *
 * @param fromIdentity 始点の節点の同一性の鍵
 * @param toIdentity 終点の節点の同一性の鍵
 * @returns 枝の同一性の鍵
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 枝の同一性の鍵を、両端の節点の同一性の組から作る。
 * @graph-connects none
 */
export function storyTransitionKeyOf(fromIdentity: string, toIdentity: string): string {
  return JSON.stringify([fromIdentity, toIdentity]);
}

/**
 * 枝の行き先の同一性の鍵。宛先の型を含める。
 *
 * 行き先の節点が無い枝（出口へ・行き先の無い止まる）は宛先の型そのものを行き先とみなす。
 * 宛先の型を鍵に含めるのは、同じ 2 業務の枝が「次へ」から「戻る」に変わったことを差分に出すため
 * である。
 *
 * @param transition 枝
 * @param identityByNodeId その側の局所 ID から同一性の鍵への対応
 * @returns 行き先の同一性の鍵。行き先の節点が図に無ければ undefined
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの枝の行き先を、宛先の型つきの同一性の鍵にする。
 * @graph-connects none
 */
export function transitionTargetIdentityOf(
  transition: FlowTransitionView,
  identityByNodeId: ReadonlyMap<number, string>,
): string | undefined {
  if (transition.to === null) return `${transition.destinationKind}:-`;
  const node = identityByNodeId.get(transition.to);
  return node === undefined ? undefined : `${transition.destinationKind}:${node}`;
}

/** 片側 1 枚分の差分の途中結果。 @graph-connects none */
interface StorySideStates {
  /** この側の図に載せる状態。鍵はこの側の局所 ID。 */
  stateByNodeId: Map<number, FlowChangeState>;
  /** この側にしか居ない業務の件数。 */
  onlyHereBusinessCount: number;
  /** この側にしか居ない節点の件数（開始 / 終了を含む）。 */
  onlyHereNodeCount: number;
  /** 両側に居て中身が変わった業務の件数。 */
  updatedBusinessCount: number;
}

/**
 * 局所 ID から同一性の鍵を引く対応を作る。
 *
 * 枝は局所 ID で両端を指すので、枝を突き合わせる前に一度ここを通して鍵を揃える。
 *
 * @param skeleton 片側の骨格
 * @returns 局所 ID から同一性の鍵への対応
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 骨格の局所 ID から節点の同一性の鍵への対応を作る。
 * @graph-connects storyNodeIdentityOf [calls] 節点の同一性の鍵
 */
function identityByNodeIdOf(skeleton: StoryFlowSkeleton): Map<number, string> {
  return new Map(skeleton.nodes.map((node) => [node.nodeId, storyNodeIdentityOf(node)]));
}

/**
 * 片側の骨格に、もう片側との突き合わせと案件の変更の行から差分の状態を付ける。
 *
 * 変更前と変更後で判定が対称なので 1 本の関数にする。片側ずつ書くと、同じ突き合わせが
 * 2 箇所に分かれ、片方だけ直された状態を作れてしまう。違うのは「相手に居ないときの状態名」
 * だけなので、それを引数で受け取る。
 *
 * 件数は節点数ではなく**業務数**で数える。案件の変更一覧は変更の行を数えるので開始 / 終了を
 * 見られない。数え方を揃えて、同じ案件・同じフローで画面によって数が食い違うのを避ける。開始 / 終了にも状態は付けるので、図の上では消えた端が読める。
 *
 * @param side 付ける側の骨格
 * @param otherIdentities もう片側に居る節点の同一性の鍵
 * @param onlyHereState この側にしか居ないときに付ける状態
 * @param touched 案件が触ったノードの索引
 * @returns この側の状態と件数
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 片側の骨格に、相手側との突き合わせと案件の変更の行から差分の状態を付ける。
 * @graph-connects storyNodeIdentityOf [calls] 節点の同一性の鍵
 */
function buildStorySideStates(
  side: StoryFlowSkeleton,
  otherIdentities: ReadonlySet<string>,
  onlyHereState: FlowChangeState,
  touched: PjTouchedIndex,
): StorySideStates {
  const stateByNodeId = new Map<number, FlowChangeState>();
  let onlyHereBusinessCount = 0;
  let onlyHereNodeCount = 0;
  let updatedBusinessCount = 0;

  for (const node of side.nodes) {
    if (!otherIdentities.has(storyNodeIdentityOf(node))) {
      stateByNodeId.set(node.nodeId, onlyHereState);
      onlyHereNodeCount += 1;
      if (node.business !== null) onlyHereBusinessCount += 1;
      continue;
    }

    if (node.business === null) continue;
    if (touched.get(node.business.id) !== 'updated') continue;

    stateByNodeId.set(node.nodeId, 'updated');
    updatedBusinessCount += 1;
  }

  return { stateByNodeId, onlyHereBusinessCount, onlyHereNodeCount, updatedBusinessCount };
}

/**
 * 鍵ごとの枝の本数を数える。
 *
 * 集合ではなく多重集合にするのは、同じ 2 業務のあいだに結末違いの枝が複数本引かれる骨格が
 * あるためである。集合で持つと本数が 1 に潰れ、片側で増えた 2 本目・3 本目が「相手にもある」
 * と見なされて差分から漏れる。
 *
 * @param skeleton 片側の骨格
 * @param identityByNodeId その側の局所 ID から同一性の鍵への対応
 * @returns 枝の鍵ごとの本数
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 枝の同一性の鍵ごとに本数を数える。
 * @graph-connects storyTransitionKeyOf [calls] 枝の同一性の鍵
 */
function countStoryTransitionKeys(
  skeleton: StoryFlowSkeleton,
  identityByNodeId: ReadonlyMap<number, string>,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const transition of skeleton.transitions) {
    const from = identityByNodeId.get(transition.from);
    const to = transitionTargetIdentityOf(transition, identityByNodeId);
    if (from === undefined || to === undefined) continue;
    const key = storyTransitionKeyOf(from, to);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

/**
 * 相手側に無い枝の ID を集める。
 *
 * 返すのは**渡した側の骨格が持つ ID** である。連番の ID は骨格ごとに振り直されるので、
 * 左右で同じ ID が別の枝を指す。片側の図にだけ使うことを前提にする。
 *
 * 相手側の本数を 1 本ずつ消費しながら走査するので、同じ組が複数本ある場合は「相手より
 * 増えた本数ぶん」だけが差分になる。どの 1 本が余分かは骨格の並び順で決まる（後ろの本が
 * 余る）。枝は本数で見るしかなく、結末のラベルで対応づけるとラベルだけ書き換えた枝が
 * 追加・削除の 2 件に化けるためである。
 *
 * @param skeleton 片側の骨格
 * @param identityByNodeId その側の局所 ID から同一性の鍵への対応
 * @param otherCounts 相手側の枝の鍵ごとの本数
 * @returns この側にしか無い枝の ID
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 相手側に無い枝の ID を、同じ組の本数まで見て集める。
 * @graph-connects storyTransitionKeyOf [calls] 枝の同一性の鍵
 */
function collectOnlyHereStoryTransitionIds(
  skeleton: StoryFlowSkeleton,
  identityByNodeId: ReadonlyMap<number, string>,
  otherCounts: ReadonlyMap<string, number>,
): Set<string> {
  const remaining = new Map(otherCounts);
  const ids = new Set<string>();

  for (const transition of skeleton.transitions) {
    const from = identityByNodeId.get(transition.from);
    const to = transitionTargetIdentityOf(transition, identityByNodeId);
    if (from === undefined || to === undefined) {
      ids.add(transition.id);
      continue;
    }
    const key = storyTransitionKeyOf(from, to);
    const left = remaining.get(key) ?? 0;
    if (left > 0) {
      remaining.set(key, left - 1);
      continue;
    }
    ids.add(transition.id);
  }

  return ids;
}

/**
 * 変更前と変更後の骨格から、左右 2 枚の図に載せる差分を導出する。
 *
 * `skeletonUnchanged` は節点と枝の増減だけで判定する。この案件が業務の中身だけを触った場合は
 * 「流れを変えていない」と扱う。読み手に伝えたいのは業務の並びと繋がりが同じであることだから
 * である。
 *
 * @param before 変更前の骨格
 * @param after 変更後の骨格
 * @param changes その案件の変更の行
 * @returns 左右 2 枚の図に載せる差分と件数
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変更前と変更後の業務フローから、左右 2 枚の図に載せる追加・変更・削除の差分を導出する。
 * @graph-connects buildPjTouchedIndex [calls] 案件の変更の行から差分の状態の索引を畳む
 * @graph-connects identityByNodeIdOf [calls] 局所 ID から節点の同一性の鍵への対応
 * @graph-connects buildStorySideStates [calls] 片側の骨格への差分状態の付与
 * @graph-connects countStoryTransitionKeys [calls] 枝の鍵ごとの本数
 * @graph-connects collectOnlyHereStoryTransitionIds [calls] 相手側に無い枝の ID の収集
 */
export function buildStoryFlowPjDiff(
  before: StoryFlowSkeleton,
  after: StoryFlowSkeleton,
  changes: readonly PjChangeRow[],
): StoryFlowPjDiff {
  const touched = buildPjTouchedIndex(changes);

  const beforeIdentityByNodeId = identityByNodeIdOf(before);
  const afterIdentityByNodeId = identityByNodeIdOf(after);
  const beforeIdentities = new Set(beforeIdentityByNodeId.values());
  const afterIdentities = new Set(afterIdentityByNodeId.values());

  const beforeSide = buildStorySideStates(before, afterIdentities, 'deleted', touched);
  const afterSide = buildStorySideStates(after, beforeIdentities, 'created', touched);

  const beforeCounts = countStoryTransitionKeys(before, beforeIdentityByNodeId);
  const afterCounts = countStoryTransitionKeys(after, afterIdentityByNodeId);
  const removedTransitionIds = collectOnlyHereStoryTransitionIds(
    before,
    beforeIdentityByNodeId,
    afterCounts,
  );
  const addedTransitionIds = collectOnlyHereStoryTransitionIds(
    after,
    afterIdentityByNodeId,
    beforeCounts,
  );

  return {
    beforeStateByNodeId: beforeSide.stateByNodeId,
    afterStateByNodeId: afterSide.stateByNodeId,
    removedTransitionIds,
    addedTransitionIds,
    createdCount: afterSide.onlyHereBusinessCount,
    updatedCount: afterSide.updatedBusinessCount,
    deletedCount: beforeSide.onlyHereBusinessCount,
    skeletonUnchanged:
      beforeSide.onlyHereNodeCount === 0 &&
      afterSide.onlyHereNodeCount === 0 &&
      removedTransitionIds.size === 0 &&
      addedTransitionIds.size === 0,
  };
}

/**
 * 案件から見た業務フローの図に、変更前との差が有るかを決める。
 *
 * **図に出る差だけを数える。** 左右比較で並べるのは図なので、図に現れない差（業務フローの名前だけの
 * 書き換えなど）で比較を開けても、同じ図が 2 枚並ぶだけになる。業務の追加と削除は節点の増減なので
 * 骨格の差（`skeletonUnchanged`）に含まれる。案件が作った・消した業務フローも、片側を空の骨格にして
 * 突き合わせるので節点が全部追加か削除になり、同じく骨格の差として出る（片側が無いことの旗を
 * 別に受け取らない）。
 *
 * @param diff 変更前と変更後の突き合わせ
 * @returns 差が有れば true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件から見た業務フローの図に、変更前との差（新設・削除・流れの変更・業務の中身の変更）が有るかを決める。
 * @graph-connects none
 */
export function hasStoryFlowPjDiff(diff: StoryFlowPjDiff): boolean {
  return !diff.skeletonUnchanged || diff.updatedCount > 0;
}

/**
 * 案件の要求から、業務ごとの「なぜそう変えたか」を畳む。鍵は業務の id。
 *
 * **理由の出どころは要求の層である**（決定 3）。案件の変更の行は理由の欄を持たないので、
 * 変更の行に結ばれた要求の文がその役目を負う。差分の色は「変わった」までしか言わず、読み手が
 * 次に知りたいのは理由だからである。
 *
 * 対応が取れない節点には**何も付けない**。要求に結ばれていない変更は実在する（要求を立てる前に
 * 触った行）ので、埋め合わせに変更の行の値を並べると、読み手はそれを「理由」として読む。
 * 空で出せば「理由が記録されていない」がそのまま伝わる。
 *
 * 同じ文が複数の要求から来たときは出現順を保って重複を除く。同じ文を並べても読む側の判断は
 * 増えない。
 *
 * @param requirements その案件が持つ要求
 * @returns 業務の id ごとの理由の一覧
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の要求を、結ばれたノードの id ごとの理由（要求の文）の一覧へ畳む。
 * @graph-connects none
 */
export function buildStoryFlowReasons(
  requirements: readonly { statement: string; nodeIds: readonly number[] }[],
): ReadonlyMap<number, readonly string[]> {
  const collected = new Map<number, string[]>();

  for (const requirement of requirements) {
    if (requirement.statement === '') continue;
    for (const nodeId of requirement.nodeIds) {
      const reasons = collected.get(nodeId) ?? [];
      if (!reasons.includes(requirement.statement)) reasons.push(requirement.statement);
      collected.set(nodeId, reasons);
    }
  }

  return collected;
}
